import { useMemo } from 'react';
import { PlayIcon, StopIcon, CircleNotchIcon } from '@phosphor-icons/react';
import { Button } from '~/components/ui/button';
import { useFeatureFlag, EDDY_AUDIO_ANSWERS_FLAG } from '~/hooks/queries/use-feature-flags';
import { useAudioAnswerStore, playKeyOf } from './audio-answer-store';
import { toSpeakableText } from './audio-answer-text';
import { primeAudio, speak, stopActivePlayback } from './audio-answer-player';

interface AudioAnswerControlProps {
  /** The chat session this message belongs to — half of the key `playing`
   *  is tracked under (see `audio-answer-store.ts`'s docblock). */
  sessionId: string;
  /** `renderKeyOf(message)` (`hooks/reconcile-history.ts`) — the caller's
   *  job, not this component's, since only the caller has the full message
   *  object. Survives the post-stream history reload (the streamed id gets
   *  promoted to the persisted row id, but the render key travels along),
   *  which is what keeps "is THIS message playing" from going stale mid-
   *  playback. */
  renderKey: string;
  /** The final-answer portion of the message's RAW (Markdown) text — the
   *  caller selects it with `finalAnswerText` (`audio-answer-text.ts`) so a
   *  multi-part reply's preamble before its tool calls is never included.
   *  Converted to speakable text here, on click. */
  text: string;
  /** This message is still streaming — no control while an answer is still
   *  being written (FRONT-2387: "not while that answer is still
   *  streaming"). Once it finishes, the control appears on its own re-render
   *  — no separate "ready" event to wait for. */
  isStreaming?: boolean;
  className?: string;
}

/**
 * Shared, presentation-free core: computes speakable text and this
 * message's own playback state, memoized by message identity + content so a
 * long conversation's other messages never pay for re-deriving this on a
 * render that has nothing to do with them. Returns `null` when there is
 * nothing to show — flag off, still streaming, or nothing worth speaking —
 * so both callers below can bail out identically.
 */
function useAudioAnswerControl({ sessionId, renderKey, text, isStreaming }: AudioAnswerControlProps) {
  const flagOn = useFeatureFlag(EDDY_AUDIO_ANSWERS_FLAG, false);
  // The key never depends on the text itself — it only needs identity — so
  // it can be computed unconditionally and cheaply, ahead of the gate below.
  const key = useMemo(() => playKeyOf(sessionId, renderKey), [sessionId, renderKey]);

  // FRONT-2387 fix round 4 (#5): a primitive selector for THIS control's own
  // key, not the whole `playing` object — every other message's button in a
  // long conversation would otherwise re-render on every playback-state tick
  // of the one message actually playing, since a new `playing` object is a
  // new reference on every store update regardless of whose key it names.
  // Reading a derived primitive here means zustand's default `Object.is`
  // equality check only lets a re-render through when THIS message's own
  // effective state actually changed. Collapsed to the three states the
  // brief/tests key on — `done`/`stopped`/`error` (this message's own past
  // playback, or none) all read as `idle`.
  const state = useAudioAnswerStore((s): 'idle' | 'loading' | 'playing' => {
    if (s.playing?.key !== key) return 'idle';
    return s.playing.state === 'loading' || s.playing.state === 'playing' ? s.playing.state : 'idle';
  });

  // FRONT-2387 fix round 4 (#5): no text work (Markdown → speech conversion)
  // when the flag is off or the message is still streaming — those are the
  // overwhelming majority of renders in any conversation, and the result is
  // thrown away immediately below either way.
  const speakable = useMemo(() => {
    if (!flagOn || isStreaming) return '';
    return toSpeakableText(text);
  }, [flagOn, isStreaming, text]);

  if (!flagOn || isStreaming || !speakable) return null;

  const isActive = state !== 'idle';

  const handleClick = () => {
    if (isActive) {
      // Only one message ever plays app-wide, so if THIS control reads
      // "Stop" it is, by construction, the one thing currently active.
      stopActivePlayback();
      return;
    }
    // The click itself IS the user gesture — resume the shared AudioContext
    // synchronously here, before any await, so iOS Safari allows playback
    // (see audio-answer-player.ts's module doc).
    primeAudio();
    useAudioAnswerStore.getState().setPlaying(key, 'loading');
    speak(speakable, {
      onState: (s) => useAudioAnswerStore.getState().setPlaying(key, s),
    });
  };

  return { state, isActive, handleClick };
}

/**
 * FRONT-2387: "Play answer" — the icon-only desktop control. Appears under
 * EVERY finished assistant reply (flag on, not streaming, non-empty
 * speakable text) — styled exactly like its neighbouring small subtle icons
 * (Copy/Save): same `Button` component, `size="icon-xs"`, `variant="ghost"`
 * and opacity/hover className. Renders `null` for a message that has
 * nothing to show, matching "flag off → no button, zero behaviour change".
 */
export function AudioAnswerButton({ sessionId, renderKey, text, isStreaming, className }: AudioAnswerControlProps) {
  const control = useAudioAnswerControl({ sessionId, renderKey, text, isStreaming });
  if (!control) return null;
  const { state, isActive, handleClick } = control;
  const label = isActive ? 'Stop' : 'Play answer';

  return (
    <Button
      type="button"
      onClick={handleClick}
      variant="ghost"
      size="icon-xs"
      tooltip={label}
      aria-label={label}
      data-testid="audio-answer-button"
      data-state={state}
      className={className ?? 'opacity-35 hover:opacity-100 text-muted-foreground'}
    >
      {state === 'loading' ? (
        <CircleNotchIcon size={13} className="animate-spin" />
      ) : isActive ? (
        <StopIcon size={13} weight="regular" />
      ) : (
        <PlayIcon size={13} weight="regular" />
      )}
    </Button>
  );
}

/**
 * FRONT-2387: the phone twin — a visible TEXT button ("Play answer"/"Stop"),
 * same control logic as the desktop icon above. `aria-label` mirrors the
 * visible text exactly rather than adding a second, different accessible
 * name.
 */
export function AudioAnswerButtonText({ sessionId, renderKey, text, isStreaming, className }: AudioAnswerControlProps) {
  const control = useAudioAnswerControl({ sessionId, renderKey, text, isStreaming });
  if (!control) return null;
  const { state, isActive, handleClick } = control;
  const label = isActive ? 'Stop' : 'Play answer';

  return (
    <Button
      type="button"
      onClick={handleClick}
      variant="outline"
      size="sm"
      className={className}
      aria-label={label}
      data-testid="audio-answer-button"
      data-state={state}
    >
      {state === 'loading' ? (
        <CircleNotchIcon size={13} className="animate-spin" />
      ) : isActive ? (
        <StopIcon size={13} weight="fill" />
      ) : (
        <PlayIcon size={13} weight="fill" />
      )}
      {label}
    </Button>
  );
}
